import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Switch,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTreatyClient } from '../../../lib/client';
import type { Menu } from '../types';
import TagsInput from './TagsInput';

export interface DishFormValues {
  name: string;
  description?: string;
  price?: number;
  tags?: string[];
  pairing_id?: string;
  status: 'enabled' | 'disabled';
}

interface DishFormModalProps {
  visible: boolean;
  initialValues?: Partial<DishFormValues>;
  currentMenu?: Menu | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const DishFormModal: React.FC<DishFormModalProps> = ({
  visible,
  initialValues,
  currentMenu,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<DishFormValues>();
  const [menuText, setMenuText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const client = useTreatyClient();
  const { symbol } = useCurrencySymbol();

  const isEditing = Boolean(currentMenu?.id);

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue(initialValues);
    }

    return () => {
      form.resetFields();
      setMenuText('');
    };
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      setLoading(true);
      const actionType = isEditing ? 'update' : 'add';

      const params = {
        name: values.name,
        description: values.description,
        price: values.price,
        pairing_id: values.pairing_id,
        tags: values.tags,
        status: values.status,
      };

      const response = isEditing
        ? await client.api.menu({ id: currentMenu!.id }).put(params)
        : await client.api.menu.create.post(params);

      if (response.data?.success) {
        message.success(
          `${values.name} ${isEditing ? 'updated' : 'added'} successfully`,
        );
        onSuccess();
        return;
      }
      message.error(response.data?.message || `Failed to ${actionType} dish`);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An error occurred while saving dish');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeText = async () => {
    if (!menuText.trim()) {
      message.warning('Please enter menu text for analysis');
      return;
    }

    setAnalyzing(true);
    try {
      const result = await client.api.menu.analyze_text.post({
        text: menuText,
      });

      if (result.data?.success) {
        message.success('Menu added successfully');
        onSuccess();
      } else {
        message.error(result.error?.value?.message || 'Menu add failed');
      }
    } catch (error) {
      console.error('Failed to add menu:', error);
      message.error('Failed to add menu, please try again later');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Dish' : 'Add New Dish'}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        isEditing ? (
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            {isEditing ? 'Save Changes' : 'Add Dish'}
          </Button>
        ) : (
          <Button
            key="analyze"
            type="primary"
            onClick={handleAnalyzeText}
            loading={analyzing}
          >
            Add
          </Button>
        ),
      ]}
      width={700}
    >
      {!isEditing ? (
        <Card className="mb-6">
          <div className="mb-4">
            <h3 className="text-base font-medium">Menu Name</h3>
            <p className="text-sm text-gray-500">Enter the full menu name</p>
          </div>
          <Input.TextArea
            rows={4}
            value={menuText}
            onChange={(e) => setMenuText(e.target.value)}
            placeholder="Enter menu name, e.g.: Classic Italian Pasta"
          />
        </Card>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter dish name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="price" label="Price">
            <InputNumber
              prefix={symbol}
              step={0.01}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name="pairing_id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <TagsInput />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            getValueFromEvent={(checked) => (checked ? 'enabled' : 'disabled')}
            getValueProps={(value) => ({ checked: value === 'enabled' })}
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default DishFormModal;
